import React, { useEffect, useState } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';

import Axios from '../../networking/intraceptor';
import { admin_announcements_active } from '../../networking/urlEndPoints';
import { useCustomSelectors } from '../../services/selectors/allSelectors';
import { ablyConnectionManager } from '../../utils/miscellaneous/ablyConnectionManager';

// Notices set on the OATRx admin "Announcements" page for this pharmacy.
// The bar sits on top of every screen, so it must never get in the way: any
// failure to load simply means no bar.

// OATRx sends a live signal the moment an admin publishes, edits, stops or
// deletes an announcement. The timer and the focus check are only the safety
// net for a signal that never arrives.
const REFRESH_INTERVAL_MS = 60 * 1000;
const LIVE_CHANNEL = 'pharmacy-notifications';
// Own event names, so none of the channel's `pharmacy-<id>` listeners hear these.
const LIVE_EVENT_PREFIX = 'admin-announcements-';
const DISMISSED_STORAGE_KEY = 'oatrx-dismissed-announcements';

const TYPE_STYLES = {
	info: {
		bar: 'bg-[#E8EDFF] text-[#26378F] border-[#C9D3FB]',
		Icon: Info,
	},
	warning: {
		bar: 'bg-[#FFF4D6] text-[#6B4300] border-[#F2D27A]',
		Icon: AlertTriangle,
	},
	critical: {
		bar: 'bg-[#C62828] text-white border-[#A31F1F]',
		Icon: AlertTriangle,
	},
};

// The admin marks bold words as **words**: "a **b** c" -> a, <strong>b</strong>, c
const renderMessage = (message) => String(message || '').split('**').map((part, index) => (
	index % 2 === 1 ? <strong key={index} className="font-bold">{part}</strong> : part
));

// An edited announcement gets a new version, so it shows again after a dismiss.
const dismissKey = (item) => `${item.id}:${item.version ?? ''}`;

const readDismissed = () => {
	try {
		const stored = JSON.parse(localStorage.getItem(DISMISSED_STORAGE_KEY));
		return Array.isArray(stored) ? stored : [];
	} catch (_) {
		return [];
	}
};

const AnnouncementBar = () => {
	const { pharmacyId } = useCustomSelectors();
	const [announcements, setAnnouncements] = useState([]);
	const [dismissed, setDismissed] = useState(readDismissed);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			try {
				const response = await Axios.get(admin_announcements_active);
				const list = response?.data?.data;
				if (!cancelled) setAnnouncements(Array.isArray(list) ? list : []);
			} catch (_) {
				if (!cancelled) setAnnouncements([]);
			}
		};

		const loadWhenVisible = () => {
			if (document.visibilityState === 'visible') load();
		};

		load();
		const timer = setInterval(loadWhenVisible, REFRESH_INTERVAL_MS);
		document.addEventListener('visibilitychange', loadWhenVisible);
		// A window that was never hidden (side by side with another) still
		// catches up when it is clicked.
		window.addEventListener('focus', load);

		// Live signal: one event for this pharmacy, one for "all pharmacies".
		// The short random wait keeps every open portal from asking the
		// server in the same instant.
		let liveTimer = null;
		const onLiveSignal = () => {
			clearTimeout(liveTimer);
			liveTimer = setTimeout(load, Math.random() * 1500);
		};
		const subscriptionIds = [];
		try {
			[pharmacyId, 'all'].filter(Boolean).forEach((target) => {
				const eventName = `${LIVE_EVENT_PREFIX}${target}`;
				const subscriptionId = `${LIVE_CHANNEL}-${eventName}-announcement-bar`;
				if (ablyConnectionManager.subscribe(LIVE_CHANNEL, eventName, onLiveSignal, subscriptionId)) {
					subscriptionIds.push(subscriptionId);
				}
			});
		} catch (_) {
			// No live connection: the timer and focus check still update the bar.
		}

		return () => {
			cancelled = true;
			clearInterval(timer);
			clearTimeout(liveTimer);
			document.removeEventListener('visibilitychange', loadWhenVisible);
			window.removeEventListener('focus', load);
			subscriptionIds.forEach((subscriptionId) => {
				try {
					ablyConnectionManager.unsubscribe(subscriptionId);
				} catch (_) {
					// already gone
				}
			});
		};
	}, [pharmacyId]);

	const dismiss = (item) => {
		const next = [...dismissed, dismissKey(item)].slice(-50);
		setDismissed(next);
		try {
			localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(next));
		} catch (_) {
			// Storage blocked: the bar just comes back on the next visit.
		}
	};

	const visible = announcements.filter((item) => item?.id && !dismissed.includes(dismissKey(item)));
	if (!visible.length) return null;

	return (
		<div role="region" aria-label="Announcements">
			{visible.map((item) => {
				const styles = TYPE_STYLES[item.type] || TYPE_STYLES.info;
				const { Icon } = styles;
				// A critical notice (e.g. access being disabled) stays on screen.
				const canDismiss = item.type !== 'critical';

				return (
					<div key={item.id}
						role={item.type === 'critical' ? 'alert' : 'status'}
						className={`flex items-center gap-3 px-4 py-2 border-b text-sm ${styles.bar}`}>
						<Icon className="size-5 shrink-0" aria-hidden="true" />

						<div className="flex-1 min-w-0 leading-snug">
							<p className="font-bold">{item.title}</p>
							<p>{renderMessage(item.message)}</p>
						</div>

						{canDismiss && (
							<button type="button"
								aria-label="Dismiss announcement"
								className="shrink-0 rounded p-0.5 opacity-75 hover:opacity-100"
								onClick={() => dismiss(item)}>
								<X className="size-4" />
							</button>
						)}
					</div>
				);
			})}
		</div>
	);
};

export default AnnouncementBar;
